import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Flow } from '@grafx-vdp/shared';

import { useTriggerFlowMutation } from '@/api/hooks.js';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { toast } from '@/components/ui/sonner.js';

interface Props {
  flow: Flow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TriggerFlowDialog({ flow, open, onOpenChange }: Props) {
  const trigger = useTriggerFlowMutation();
  const navigate = useNavigate();
  const [lastScheduledAt, setLastScheduledAt] = useState<string | null>(null);

  // `trigger` is a fresh object every render; only `reset` is stable. Depending
  // on `trigger` here caused an infinite render loop while the dialog is closed.
  const { reset } = trigger;
  useEffect(() => {
    if (!open) {
      reset();
      setLastScheduledAt(null);
    }
  }, [open, reset]);

  if (!flow) return null;

  const handleConfirm = async () => {
    try {
      const result = await trigger.mutateAsync({ flowId: flow.id });
      setLastScheduledAt(result.message.scheduledAt);
      toast.success('Trigger accepted', {
        description: `${flow.name} was enqueued at ${new Date(result.message.scheduledAt).toLocaleTimeString()}.`,
      });
    } catch (e) {
      toast.error('Trigger failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trigger {flow.name}?</DialogTitle>
          <DialogDescription>
            Enqueues a manual <code className="rounded bg-muted px-1">flow.run.enqueued</code>{' '}
            message. The workflow-runner picks it up on the next queue poll and starts a new
            FlowRun.
          </DialogDescription>
        </DialogHeader>

        {trigger.isError ? (
          <p className="text-sm text-destructive">
            {trigger.error instanceof Error ? trigger.error.message : 'Trigger failed.'}
          </p>
        ) : null}

        {lastScheduledAt ? (
          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            Accepted at {new Date(lastScheduledAt).toLocaleString()}. The new run will appear in
            the Runs tab shortly.
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {lastScheduledAt ? 'Close' : 'Cancel'}
          </Button>
          {lastScheduledAt ? (
            <Button
              onClick={() => {
                onOpenChange(false);
                navigate('/runs');
              }}
            >
              View runs
            </Button>
          ) : (
            <Button onClick={handleConfirm} disabled={trigger.isPending}>
              {trigger.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Trigger now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
